<template>
  <details class="bundle-links">
    <summary class="bundle-links__summary">
      <span class="material-icons bundle-links__icon" aria-hidden="true">link</span>
      <span class="bundle-links__heading">
        <strong>Link bundle Vitastrong</strong>
        <span>Da copiare e inviare via email o WhatsApp</span>
      </span>
      <span class="bundle-links__count">{{ bundles.length }}</span>
      <span class="material-icons bundle-links__chevron" aria-hidden="true">expand_more</span>
    </summary>

    <div class="bundle-links__content">
      <div class="bundle-links__toolbar">
        <label class="bundle-links__search">
          <span class="material-icons" aria-hidden="true">search</span>
          <input v-model="search" type="search" placeholder="Cerca bundle o prodotto" aria-label="Cerca bundle o prodotto" />
        </label>
        <a class="bundle-links__page" href="https://www.fusco.fit/integrazione/index.html" target="_blank" rel="noopener noreferrer">
          Pagina integrazione
          <span class="material-icons" aria-hidden="true">open_in_new</span>
        </a>
      </div>
      <p class="bundle-links__hint">Copia il link diretto al bundle, poi incollalo nel messaggio per il cliente.</p>
      <p class="bundle-links__sr-only" role="status" aria-live="polite">{{ copyMessage }}</p>

      <ul v-if="filteredBundles.length" class="bundle-links__list" aria-label="Link dei bundle">
        <li v-for="bundle in filteredBundles" :key="bundle.id" class="bundle-links__item">
          <div class="bundle-links__description">
            <h2>{{ bundle.title }}</h2>
            <p>{{ bundle.products.join(' · ') }}</p>
          </div>
          <a class="bundle-links__url" :href="bundle.purchaseUrl" target="_blank" rel="noopener noreferrer" :aria-label="`Apri ${bundle.title} su Vitastrong`">
            {{ bundle.purchaseUrl }}
            <span class="material-icons" aria-hidden="true">open_in_new</span>
          </a>
          <button
            type="button"
            class="bundle-links__copy"
            :class="{ 'bundle-links__copy--done': copiedId === bundle.id }"
            :disabled="Boolean(copyingId)"
            :aria-label="`Copia link ${bundle.title}`"
            @click="copyBundleLink(bundle)"
          >
            <span class="material-icons" aria-hidden="true">{{ copiedId === bundle.id ? 'check' : 'content_copy' }}</span>
            {{ copyingId === bundle.id ? 'Copio…' : copiedId === bundle.id ? 'Copiato' : 'Copia link' }}
          </button>
          <div v-if="copyErrorId === bundle.id" class="bundle-links__error" role="alert">
            Copia automatica non disponibile. Seleziona e copia questo link:
            <input :value="bundle.purchaseUrl" readonly :aria-label="`Link da copiare per ${bundle.title}`" @focus="$event.target.select()" />
          </div>
        </li>
      </ul>
      <p v-else class="bundle-links__empty">Nessun bundle trovato. Prova con un altro nome o prodotto.</p>
    </div>
  </details>
</template>

<script>
import { computed, onBeforeUnmount, ref } from 'vue';
import { VITASTRONG_BUNDLES } from '@/lib/integrazione';

export default {
  name: 'DashboardBundleLinks',
  setup() {
    const search = ref('');
    const copiedId = ref('');
    const copyingId = ref('');
    const copyErrorId = ref('');
    const copyMessage = ref('');
    let feedbackTimer;
    let disposed = false;
    const normalize = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('it');
    const filteredBundles = computed(() => {
      const words = normalize(search.value).trim().split(/\s+/).filter(Boolean);
      return VITASTRONG_BUNDLES.filter(bundle => {
        const text = normalize([bundle.title, ...bundle.products].join(' '));
        return words.every(word => text.includes(word));
      });
    });

    // Also support staff using the app through a browser without Clipboard API access.
    const fallbackCopy = text => {
      const focused = document.activeElement;
      const input = document.createElement('textarea');
      input.value = text;
      input.readOnly = true;
      input.style.cssText = 'position:fixed;top:0;left:-9999px;font-size:16px';
      document.body.appendChild(input);
      try {
        input.focus({ preventScroll: true });
        input.select();
        input.setSelectionRange(0, text.length);
        if (!document.execCommand('copy')) throw new Error('Copy unavailable');
      } finally {
        input.remove();
        focused?.focus?.({ preventScroll: true });
      }
    };

    const copyBundleLink = async bundle => {
      if (copyingId.value) return;
      clearTimeout(feedbackTimer);
      copiedId.value = '';
      copyErrorId.value = '';
      copyMessage.value = '';
      copyingId.value = bundle.id;
      try {
        try {
          if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
          await navigator.clipboard.writeText(bundle.purchaseUrl);
        } catch {
          fallbackCopy(bundle.purchaseUrl);
        }
        if (disposed) return;
        copiedId.value = bundle.id;
        copyMessage.value = `Link di ${bundle.title} copiato. Puoi incollarlo in email o WhatsApp.`;
        feedbackTimer = setTimeout(() => { copiedId.value = ''; }, 3000);
      } catch {
        if (!disposed) copyErrorId.value = bundle.id;
      } finally {
        copyingId.value = '';
      }
    };

    onBeforeUnmount(() => { disposed = true; clearTimeout(feedbackTimer); });
    return { bundles: VITASTRONG_BUNDLES, search, filteredBundles, copiedId, copyingId, copyErrorId, copyMessage, copyBundleLink };
  },
};
</script>

<style scoped>
.bundle-links { margin: 12px 0 20px; border: 1px solid #cddbf3; border-radius: 14px; background: #fff; color: #17243b; overflow: hidden; text-align: left; }
.bundle-links * { box-sizing: border-box; }
.bundle-links__summary { display: flex; align-items: center; gap: 12px; min-height: 76px; padding: 16px 18px; background: #edf3ff; cursor: pointer; list-style: none; }
.bundle-links__summary::-webkit-details-marker { display: none; }
.bundle-links__summary::marker { content: ''; }
.bundle-links__icon { display: grid; place-items: center; flex: 0 0 40px; width: 40px; height: 40px; border-radius: 11px; background: #dce8ff; color: #2858b7; }
.bundle-links__heading { display: grid; gap: 4px; flex: 1; min-width: 0; line-height: 1.35; }
.bundle-links__heading strong { font-size: 17px; color: #17243b; }
.bundle-links__heading > span { font-size: 13px; color: #516485; }
.bundle-links__count { border-radius: 20px; padding: 4px 9px; background: #fff; color: #2858b7; font-size: 13px; font-weight: 750; }
.bundle-links__chevron { flex-shrink: 0; color: #516485; transition: transform .15s; }
.bundle-links[open] .bundle-links__chevron { transform: rotate(180deg); }
.bundle-links__content { padding: 18px; border-top: 1px solid #dbe5f5; }
.bundle-links__toolbar { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.bundle-links__search { display: flex; align-items: center; gap: 9px; flex: 1 1 250px; margin: 0; padding: 0 12px; min-height: 46px; border: 1px solid #cbd5e4; border-radius: 10px; background: #fff; color: #61738c; }
.bundle-links .bundle-links__search input { width: 100%; min-width: 0; min-height: 44px; margin: 0; padding: 8px 0; border: 0 !important; border-radius: 0 !important; background: transparent !important; box-shadow: none; color: #17243b; font: inherit; font-size: 16px !important; font-weight: 400 !important; outline: none; }
.bundle-links__search:focus-within { outline: 2px solid #2858b7; outline-offset: 2px; }
.bundle-links__page { display: inline-flex; align-items: center; gap: 6px; min-height: 44px; font-size: 13px; font-weight: 650; color: #2858b7; text-decoration: underline; text-underline-offset: 3px; }
.bundle-links__page .material-icons, .bundle-links__url .material-icons { font-size: 16px; flex-shrink: 0; }
.bundle-links__hint { margin: 12px 0 16px; color: #64748b; font-size: 13px; line-height: 1.5; }
.bundle-links__list { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 380px), 1fr)); gap: 12px; list-style: none; padding: 0; margin: 0; }
.bundle-links__item { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: start; gap: 12px; padding: 16px; margin: 0; border: 1px solid #dce3ed; border-radius: 11px; background: #fafbfd; }
.bundle-links__description { min-width: 0; grid-column: 1 / -1; }
.bundle-links__description h2 { margin: 0 0 6px; color: #17243b; font-size: 16px; font-weight: 750; line-height: 1.35; text-align: left; }
.bundle-links__description p { margin: 0; color: #5a6c84; font-size: 13px; line-height: 1.5; overflow-wrap: anywhere; }
.bundle-links__url { display: flex; align-items: center; align-self: center; gap: 6px; min-width: 0; min-height: 44px; font-size: 12px; line-height: 1.45; color: #486184; overflow-wrap: anywhere; text-decoration: underline; text-underline-offset: 3px; }
.bundle-links .bundle-links__copy { display: inline-flex; justify-content: center; align-items: center; gap: 7px; min-width: 120px; min-height: 44px; margin: 0; padding: 10px 12px; border: 1px solid #2858b7; border-radius: 9px; background: #2858b7; color: #fff; font: inherit; font-size: 13px; font-weight: 700; line-height: 1.4; cursor: pointer; box-shadow: none; transform: none; }
.bundle-links .bundle-links__copy:hover { background: #214b9d; }
.bundle-links .bundle-links__copy--done { background: #e3edff; color: #214b9d; }
.bundle-links .bundle-links__copy--done:hover { background: #d7e5ff; }
.bundle-links .bundle-links__copy:disabled { opacity: .65; cursor: wait; }
.bundle-links__copy .material-icons { font-size: 18px; }
.bundle-links .material-icons { color: inherit !important; }
.bundle-links__error { grid-column: 1 / -1; color: #9a4519; font-size: 13px; line-height: 1.5; }
.bundle-links .bundle-links__error input { width: 100%; min-width: 0; margin: 8px 0 0; padding: 10px; border: 1px solid #cbd5e4; border-radius: 8px; background: #fff; color: #17243b; font-size: 16px; }
.bundle-links__empty { margin: 16px 0 0; padding: 20px; border-radius: 10px; background: #f4f6fa; color: #516485; font-size: 14px; }
.bundle-links :is(summary, button, a):focus-visible { outline: 2px solid #2858b7; outline-offset: -3px; }
.bundle-links__sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
@media (max-width: 600px) {
  .bundle-links__summary { gap: 9px; padding: 14px 12px; }
  .bundle-links__icon { display: none; }
  .bundle-links__heading strong { font-size: 16px; }
  .bundle-links__heading > span { font-size: 12px; }
  .bundle-links__content { padding: 12px; }
  .bundle-links__toolbar { gap: 4px; }
  .bundle-links__search { flex-basis: 100%; }
  .bundle-links__item { padding: 13px; gap: 10px; }
  .bundle-links__url { grid-column: 1 / -1; min-height: 30px; }
  .bundle-links .bundle-links__copy { grid-column: 1 / -1; width: 100%; }
}
@media (prefers-reduced-motion: reduce) { .bundle-links__chevron { transition: none; } }
</style>
